// Storefront Styles — named, reusable element style presets.
//
// This file is the ADMIN-SIDE hand-mirror of the shopper renderer's
// `ac-storefront-web: src/Components/Helpers/stylesConfig.ts` (same mirroring rule as
// searchPageConfig.ts / homePageConfig.ts / localizationConfig.ts). Keep the resolution
// semantics below byte-for-byte equivalent to the renderer or the editor preview lies.
//
// ── Reference mechanism ──────────────────────────────────────────────────────────────────
// Every place an element style lives today — a page-config key (`addToCartButton: {...}`) or
// a section StyleProps dictionary — the dict may carry a reserved `$styleRef: "<styleId>"`
// key naming a Storefront Style.
//
//   resolved = { ...style.parts[part], ...local minus $styleRef }     // preset under, local wins
//
//  * The part is implied by the KEY the style lives on (`addToCartButton` -> `base`,
//    `addToCartLabel` -> `label`, `cardHover` -> `hover`, `productStyleProps` -> `card`, …).
//    STYLE_TARGET_BY_KEY below is that map; when a caller supplies no part we fall back to the
//    referenced style's DEFAULT_STYLE_PART so an un-mapped key still resolves sensibly.
//  * A dangling ref (style deleted) resolves to the local dict only — never an error.
//  * `$styleRef` is ALWAYS stripped before the result reaches a React `style` prop / CSS text.
//  * The API never parses refs — the config blobs stay opaque to it.
//
// ── Delivery ─────────────────────────────────────────────────────────────────────────────
// At runtime the shopper reads the composed, read-only `IStorefrontSettings.stylesConfig`
// blob (`{"styles":[…]}`, storefront styles + org globals). The admin editor instead works
// from the GET .../styles list plus the Styles panel's unsaved drafts, and publishes the
// merged list into the module registry below so the (pure, non-hook) style builders in
// styleControls.tsx resolve refs without every call site having to thread a styles array.

import { IStorefrontStyle, IStorefrontStyleParts, StorefrontStyleElementType } from '../../../models/Storefront';

/** The reserved key that turns an ordinary style dict into a reference to a named style. */
export const STYLE_REF_KEY = '$styleRef';

/** `IStorefrontStyle.storefront` value marking an org-wide (global) style. */
export const GLOBAL_STYLE_SCOPE = 'global';

/**
 * The element-style PRESET types: the ones an element can point at with a `$styleRef`. `palette`
 * and `fontPack` are deliberately not here - nothing references them by id; they are referenced by
 * TOKEN (`$palette:accent`) and resolved from whichever doc is active.
 */
export const STYLE_PRESET_ELEMENT_TYPES: StorefrontStyleElementType[] = ['button', 'card', 'section', 'typography', 'form'];

/** The token docs: one active palette and one active font pack per storefront. */
export const STYLE_TOKEN_ELEMENT_TYPES: StorefrontStyleElementType[] = ['palette', 'fontPack'];

/** True for the two token docs, which get bespoke panel rows and editors rather than part groups. */
export const isTokenElementType = (elementType?: string): boolean =>
    elementType === 'palette' || elementType === 'fontPack';

/** Every category the Styles panel lists, in display order. */
export const STYLE_ELEMENT_TYPES: StorefrontStyleElementType[] = [...STYLE_PRESET_ELEMENT_TYPES, ...STYLE_TOKEN_ELEMENT_TYPES];

/** Panel category headings, in display order. */
export const STYLE_ELEMENT_TYPE_LABELS: Record<StorefrontStyleElementType, string> = {
    button: 'Buttons',
    card: 'Product Cards',
    section: 'Sections',
    typography: 'Typography',
    form: 'Forms',
    palette: 'Color Palettes',
    fontPack: 'Font Packs',
};

/** Singular noun for buttons/dialogs ("New button style", "Delete this card style"). */
export const STYLE_ELEMENT_TYPE_SINGULAR: Record<StorefrontStyleElementType, string> = {
    button: 'Button',
    card: 'Product Card',
    section: 'Section',
    typography: 'Typography',
    form: 'Form',
    palette: 'Color Palette',
    fontPack: 'Font Pack',
};

/**
 * Conventional parts per element type. Unknown parts are ignored by renderers, so this list is
 * forward-compatible — a newer renderer can start honoring a part an older editor never wrote.
 */
/**
 * The `homePageConfig` keys a SECTION may override (AC-1165), paired with the part of a `section` style
 * that carries each one.
 *
 * These are the styling layers that paint INSIDE a section — the product card and its name, price and
 * image, the add-to-cart button, the side-scroll chrome, and the knocked-out block behind the section
 * title. They lived only on the page, one set shared by every section, which is why a section could not
 * be styled differently below its band and a saved section style could not carry the look it was taken
 * from.
 *
 * The part names are prefixed where a bare one would collide with a part a section style already has:
 * `title` is the section's own title BAND, so the card's name is `productTitle`.
 *
 * `card` is here even though `productStyleProps` is already a per-section card bag. The two layer, in
 * this order: page config, then this override, then `productStyleProps`. The override exists so a section
 * STYLE can carry card styling; `productStyleProps` stays what the card pickers write.
 */
export const SECTION_OVERRIDE_PARTS: Record<string, string> = {
    card: 'productCard',
    cardHover: 'productCardHover',
    title: 'productTitle',
    price: 'productPrice',
    image: 'productImage',
    addToCartButton: 'addToCartButton',
    scrollButton: 'scrollButton',
    scrollbar: 'scrollbar',
    moreLikeThisButton: 'moreLikeThisButton',
    sectionTitleText: 'sectionTitleText',
};

/** The `homePageConfig` keys a section may override, in a stable order. */
export const SECTION_OVERRIDE_KEYS = Object.keys(SECTION_OVERRIDE_PARTS);

export const STYLE_PARTS: Record<StorefrontStyleElementType, string[]> = {
    button: ['base', 'hover', 'label'],
    card: ['card', 'title', 'price', 'image', 'hover'],
    // band/title/header/footer/product are the section's own container; the rest are the page-level
    // layers a section can now override (SECTION_OVERRIDE_PARTS).
    section: ['band', 'title', 'header', 'footer', 'product',
        ...Object.values(SECTION_OVERRIDE_PARTS)],
    typography: ['heading', 'body'],
    form: ['input', 'label', 'focus'],
    // The token docs carry a single dictionary each, keyed by slot rather than by CSS property.
    // They never render the generic part-group editor, so these exist only for completeness.
    palette: ['colors'],
    fontPack: ['fonts'],
};

/** Human labels for the part tabs in the style editor. */
export const STYLE_PART_LABELS: Record<string, string> = {
    base: 'Base',
    hover: 'Hover',
    label: 'Label',
    card: 'Card',
    title: 'Title',
    price: 'Price',
    image: 'Image',
    band: 'Band',
    header: 'Header',
    footer: 'Footer',
    product: 'Product',
    productCard: 'Product Card',
    productCardHover: 'Product Card (hover)',
    productTitle: 'Product Name',
    productPrice: 'Product Price',
    productImage: 'Product Image',
    addToCartButton: 'Add to Cart Button',
    scrollButton: 'Scroll Button',
    scrollbar: 'Scrollbar',
    moreLikeThisButton: 'More Like This Button',
    sectionTitleText: 'Section Title Text',
    heading: 'Heading',
    body: 'Body',
    input: 'Input',
    focus: 'Focus',
    colors: 'Colors',
    fonts: 'Fonts',
};

/**
 * Fluent MDL2 icon per part, shown on the part-group headers in the Styles panel. Part keys are
 * shared across element types (`title` is both a card's name and a section's title bar, `label` is
 * both a button's pill and a form's caption, `hover` covers buttons and cards), so one icon per key
 * covers every owner.
 *
 * Every name here was verified against the registered icon set this app initializes —
 * @fluentui/font-icons-mdl2's `fabric-icons.js` (unquoted keys) plus `fabric-icons-0..17.js`
 * (quoted keys), 1801 names total. An unregistered name renders nothing, silently. A part may be
 * omitted; the group then simply has no icon.
 */
export const STYLE_PART_ICONS: Record<string, string> = {
    base: 'ButtonControl',
    hover: 'TouchPointer',
    label: 'Tag',
    card: 'GridViewMedium',
    title: 'Header1',
    price: 'Money',
    image: 'Photo2',
    band: 'RectangleShape',
    // Header/Footer are MDL2's matched document-region pair — the top and bottom strips.
    header: 'Header',
    footer: 'Footer',
    product: 'ProductList',
    heading: 'Header1',
    body: 'TextDocument',
    input: 'TextField',
    focus: 'Focus',
    colors: 'Color',
    fonts: 'Font',
};

/** The part used when a ref carries no key context (see resolveElementStyle). */
export const DEFAULT_STYLE_PART: Record<StorefrontStyleElementType, string> = {
    button: 'base',
    card: 'card',
    section: 'band',
    typography: 'body',
    form: 'input',
    palette: 'colors',
    fontPack: 'fonts',
};

/** Which parts of a style are rendered as `:hover` rules rather than inline styles. */
export const HOVER_PARTS = ['hover', 'focus'];

export interface IStyleTarget {
    elementType: StorefrontStyleElementType;
    part: string;
}

/**
 * key -> (elementType, part). The key is the page-config property name (`addToCartButton`,
 * `cardHover`, …) or a section StyleProps property name (`styleProps`, `productStyleProps`, …).
 * This is the plan's "the part is implied by the key" mapping, in one place so the pencil
 * popovers can be wired generically instead of hand-listing every key.
 */
export const STYLE_TARGET_BY_KEY: Record<string, IStyleTarget> = {
    // ── Buttons ──────────────────────────────────────────────────────────────────────────
    addToCartButton: { elementType: 'button', part: 'base' },
    addToCartLabel: { elementType: 'button', part: 'label' },
    checkoutButton: { elementType: 'button', part: 'base' },
    secondaryButton: { elementType: 'button', part: 'base' },
    placeOrderButton: { elementType: 'button', part: 'base' },
    continueButton: { elementType: 'button', part: 'base' },
    scrollButton: { elementType: 'button', part: 'base' },
    menuButton: { elementType: 'button', part: 'base' },
    moreLikeThisButton: { elementType: 'button', part: 'base' },
    buttonStyle: { elementType: 'button', part: 'base' }, // Browse All band / banner overlay CTA

    // ── Product cards ────────────────────────────────────────────────────────────────────
    card: { elementType: 'card', part: 'card' },
    title: { elementType: 'card', part: 'title' },
    price: { elementType: 'card', part: 'price' },
    image: { elementType: 'card', part: 'image' },
    cardHover: { elementType: 'card', part: 'hover' },
    linkedCard: { elementType: 'card', part: 'card' },

    // ── Sections (the five section StyleProps dictionaries) ──────────────────────────────
    styleProps: { elementType: 'section', part: 'band' },
    titleStyleProps: { elementType: 'section', part: 'title' },
    headerStyleProps: { elementType: 'section', part: 'header' },
    footerStyleProps: { elementType: 'section', part: 'footer' },
    // Product cards INSIDE a section reference a CARD-type style, not the section style.
    productStyleProps: { elementType: 'card', part: 'card' },

    // ── Forms ────────────────────────────────────────────────────────────────────────────
    quantityInput: { elementType: 'form', part: 'input' },
    searchBar: { elementType: 'form', part: 'input' },

    // ── Header account / sign-in persona ─────────────────────────────────────────────────
    account: { elementType: 'button', part: 'base' },
    accountName: { elementType: 'typography', part: 'body' },
    accountSubtext: { elementType: 'typography', part: 'body' },
    accountAvatar: { elementType: 'card', part: 'image' },
    accountPanel: { elementType: 'section', part: 'band' },

    // ── Auth pages (sign-in / sign-up / token / confirm) ─────────────────────────────────
    loginPage: { elementType: 'section', part: 'band' },
    loginBox: { elementType: 'card', part: 'card' },
    loginLogo: { elementType: 'card', part: 'image' },
    loginHeading: { elementType: 'typography', part: 'heading' },
    loginIntro: { elementType: 'typography', part: 'body' },
    loginInput: { elementType: 'form', part: 'input' },
    loginPrimaryButton: { elementType: 'button', part: 'base' },
    loginSecondaryButton: { elementType: 'button', part: 'base' },
    loginMessage: { elementType: 'section', part: 'band' },

    // ── Notification bars ────────────────────────────────────────────────────────────────
    // The band is a section-type `band` (it is a full-width colored strip, exactly what that part
    // means elsewhere); its message is typography, and its two controls are buttons.
    notificationBar: { elementType: 'section', part: 'band' },
    notificationMessage: { elementType: 'typography', part: 'body' },
    notificationLink: { elementType: 'button', part: 'label' },
    notificationCloseButton: { elementType: 'button', part: 'base' },
};

// NOTE: several key NAMES mean different things in different config blobs — `title` is the
// product-card name on the home/search pages but a page heading on the cart page; `image` is the
// card image on the search page but the hero image on the product page. Editors therefore look
// keys up through `styleTargetForConfigKey` (styleBulkApply.ts), which narrows this table by the
// owning config. This table stays the only place an element type/part is declared.

/** The (elementType, part) a style picker on `key` should read/write, or undefined for un-mapped keys. */
export const styleTargetForKey = (key?: string | null): IStyleTarget | undefined =>
    key ? STYLE_TARGET_BY_KEY[key] : undefined;

/** The part implied by a config key (see STYLE_TARGET_BY_KEY). */
export const stylePartForKey = (key?: string | null): string | undefined => styleTargetForKey(key)?.part;

// ── The composed `stylesConfig` blob ─────────────────────────────────────────────────────

/** One entry of the composed blob. `scope` is the flattened form of the doc's `storefront` field. */
export interface IStylesConfigEntry {
    id: string;
    name: string;
    elementType: StorefrontStyleElementType;
    scope?: 'storefront' | 'global';
    sortOrder?: number;
    /** Present on `palette` / `fontPack` entries - the one active doc of its type. */
    isActive?: boolean;
    parts?: IStorefrontStyleParts;
}

export interface IStylesConfig {
    styles?: IStylesConfigEntry[];
}

/**
 * Parse the read-only composed blob into the same shape the editor's GET list returns, so both
 * feed one resolver. Invalid/absent JSON yields an empty list (zero behavior change for legacy
 * storefronts that have no styles).
 */
export function parseStylesConfig(raw?: string): IStorefrontStyle[] {
    if (!raw) return [];
    let parsed: IStylesConfig;
    try {
        parsed = JSON.parse(raw) as IStylesConfig;
    } catch {
        return [];
    }
    if (!parsed || !Array.isArray(parsed.styles)) return [];
    return parsed.styles
        .filter((s) => s && typeof s.id === 'string' && !!s.id)
        .map((s) => ({
            id: s.id,
            name: s.name ?? '',
            elementType: s.elementType,
            storefront: s.scope === 'global' ? GLOBAL_STYLE_SCOPE : (s.scope ?? ''),
            sortOrder: s.sortOrder ?? 0,
            // Kept strictly boolean here so a JSON `"true"` / `1` can never activate a doc; every
            // read site additionally compares `=== true` (see getActiveDoc).
            isActive: s.isActive === true ? true : undefined,
            parts: s.parts ?? {},
        }));
}

/** Serialize a styles list back into the composed blob shape (used by tests/tooling, not by saves). */
export function serializeStylesConfig(styles: IStorefrontStyle[]): string {
    const config: IStylesConfig = {
        styles: styles.map((s) => ({
            id: s.id,
            name: s.name,
            elementType: s.elementType,
            scope: isGlobalStyle(s) ? 'global' : 'storefront',
            sortOrder: s.sortOrder,
            isActive: s.isActive === true ? true : undefined,
            parts: s.parts,
        })),
    };
    return JSON.stringify(config);
}

// ── Refs ─────────────────────────────────────────────────────────────────────────────────

/** True when the style doc is org-wide rather than owned by one storefront. */
export const isGlobalStyle = (style?: Pick<IStorefrontStyle, 'storefront'>): boolean =>
    style?.storefront === GLOBAL_STYLE_SCOPE;

/** The style id referenced by an element's style dict, if any. */
export function getStyleRef(local: unknown): string | undefined {
    if (!local || typeof local !== 'object') return undefined;
    const ref = (local as Record<string, unknown>)[STYLE_REF_KEY];
    return typeof ref === 'string' && ref !== '' ? ref : undefined;
}

/**
 * Remove the reserved `$styleRef` key. ALWAYS call this before spreading a style dict into a
 * React `style` prop or turning it into CSS text — `$styleRef` is not a CSS property.
 * Returns the input unchanged (same reference) when there is nothing to strip.
 */
export function stripStyleRef<T>(local: T): T {
    if (!local || typeof local !== 'object') return local;
    if (!(STYLE_REF_KEY in (local as Record<string, unknown>))) return local;
    const copy = { ...(local as Record<string, unknown>) };
    delete copy[STYLE_REF_KEY];
    return copy as unknown as T;
}

/**
 * How many real style properties the dict carries of its own — everything except the reserved
 * `$styleRef` bookkeeping key and cleared (undefined/'') values. Drives the "Advanced overrides (3)"
 * badge so a collapsed group still tells you something is set underneath.
 */
export function countLocalOverrides(local: unknown): number {
    if (!local || typeof local !== 'object') return 0;
    return Object.entries(local as Record<string, unknown>)
        .filter(([k, v]) => k !== STYLE_REF_KEY && v !== undefined && v !== '').length;
}

/** Does the dict carry any styling of its own beyond the ref (i.e. is it overriding the preset)? */
export function hasLocalOverrides(local: unknown): boolean {
    return countLocalOverrides(local) > 0;
}

export function findStyle(styles: IStorefrontStyle[] | undefined, id?: string): IStorefrontStyle | undefined {
    if (!id || !styles) return undefined;
    return styles.find((s) => s.id === id);
}

// ── Palette + font pack tokens ───────────────────────────────────────────────────────────
//
// ADMIN MIRROR of the shopper renderer's "Palette + font pack tokens" section
// (ac-storefront-web: src/Components/Helpers/stylesConfig.ts). Keep the semantics identical or
// the editor preview paints something the shopper will not.
//
// A `palette` doc (parts.colors) and a `fontPack` doc (parts.fonts) turn the storefront's brand
// into named slots. Any style VALUE anywhere styles flow may reference one:
//
//   "$palette:accent"          -> the active palette's colors.accent
//   "$palette:surface@0.55"    -> rgba(r,g,b,0.55) from colors.surface
//   "$font:heading"            -> the active font pack's fonts.heading
//
// A token that cannot be resolved (no active doc, unknown slot, unparseable hex, bad alpha) DROPS
// the property entirely, so the element falls back to whatever it renders without it. Anything
// that is not a token passes through untouched, and a dict with no tokens comes back BY IDENTITY.

/** The palette's colour slots, in emit order. Also the allow-list for token resolution. */
export const PALETTE_SLOTS = [
    'background', 'surface', 'text', 'heading', 'accent', 'accentText', 'menuBar', 'menuBarText', 'border',
] as const;
export type PaletteSlot = typeof PALETTE_SLOTS[number];

export const FONT_SLOTS = ['heading', 'body'] as const;
export type FontSlot = typeof FONT_SLOTS[number];

export const PALETTE_TOKEN_PREFIX = '$palette:';
export const FONT_TOKEN_PREFIX = '$font:';

/** Friendly slot names for the palette editor rows and the picker swatch strip. */
export const PALETTE_SLOT_LABELS: Record<PaletteSlot, string> = {
    background: 'Background',
    surface: 'Surface',
    text: 'Text',
    heading: 'Headings',
    accent: 'Accent',
    accentText: 'Accent Text',
    menuBar: 'Menu Bar',
    menuBarText: 'Menu Bar Text',
    border: 'Borders',
};

export const FONT_SLOT_LABELS: Record<FontSlot, string> = {
    heading: 'Heading font',
    body: 'Body font',
};

/**
 * The slots shown as the compact swatch strip on a palette's list row - the ones that carry the
 * character of the palette. The editor still exposes every slot in PALETTE_SLOTS.
 */
export const PALETTE_STRIP_SLOTS: PaletteSlot[] = ['background', 'surface', 'text', 'accent', 'accentText', 'menuBar'];

/**
 * Starting colours for a brand-new palette: neutral greys with the app's brand green as the accent,
 * so a fresh palette is immediately usable (and immediately visible) rather than a row of blanks.
 */
export const DEFAULT_PALETTE_COLORS: Record<PaletteSlot, string> = {
    background: '#ffffff',
    surface: '#f7f7f7',
    text: '#242424',
    heading: '#1b1a19',
    accent: '#789c2f',
    accentText: '#ffffff',
    menuBar: '#ffffff',
    menuBarText: '#242424',
    border: '#e1dfdd',
};

/**
 * Starting fonts for a brand-new font pack. Spelled out rather than imported from FontPicker's
 * FONT_STACKS so this file keeps its "mirror of the renderer helper, models-only imports" shape -
 * the strings are copies of the `Georgia` and `System UI` stacks there.
 */
export const DEFAULT_FONT_PACK_FONTS: Record<FontSlot, string> = {
    heading: 'Georgia, serif',
    body: 'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
};

// Alpha must be a plain unsigned decimal in [0, 1] - no sign, no exponent, no whitespace. Number()
// alone is far too permissive here ('' and ' ' both coerce to 0).
const ALPHA_PATTERN = /^\d*\.?\d+$/;

/**
 * #rgb / #rrggbb only. Anything else (named colour, rgb(), gradient, CSS var) is deliberately not
 * parseable, which is what makes an @alpha token on it drop rather than emit nonsense.
 */
export function hexRgb(color: unknown): { r: number; g: number; b: number } | undefined {
    if (typeof color !== 'string') return undefined;
    const match = color.trim().match(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);
    if (!match) return undefined;
    const hex = match[1].length === 3
        ? `${match[1][0]}${match[1][0]}${match[1][1]}${match[1][1]}${match[1][2]}${match[1][2]}`
        : match[1];
    const value = parseInt(hex, 16);
    return { r: (value >> 16) & 255, g: (value >> 8) & 255, b: value & 255 };
}

/**
 * The active doc for an element type. At most one is active; when both a storefront-scoped and a
 * global doc claim it, the storefront's own wins (the renderer's `scope === 'storefront'` test -
 * here anything that isn't `global`, which also covers the `{id}-preview` staged scope). Within a
 * scope the first in list order wins, so a malformed list still resolves deterministically.
 */
function getActiveDoc(styles: IStorefrontStyle[] | undefined, elementType: string): IStorefrontStyle | undefined {
    if (!styles) return undefined;
    let global: IStorefrontStyle | undefined;
    for (const style of styles) {
        if (style.elementType !== elementType || style.isActive !== true) continue;
        if (!isGlobalStyle(style)) return style;
        if (!global) global = style;
    }
    return global;
}

export function getActivePalette(styles: IStorefrontStyle[] | undefined = getActiveStorefrontStyles()): IStorefrontStyle | undefined {
    return getActiveDoc(styles, 'palette');
}

export function getActiveFontPack(styles: IStorefrontStyle[] | undefined = getActiveStorefrontStyles()): IStorefrontStyle | undefined {
    return getActiveDoc(styles, 'fontPack');
}

function dictOf(style: IStorefrontStyle | undefined, key: string): Record<string, unknown> | undefined {
    const value = style?.parts?.[key];
    return value && typeof value === 'object' ? (value as Record<string, unknown>) : undefined;
}

/** The active palette's slot -> colour map, or undefined when no palette is active. */
export function activePaletteColors(styles?: IStorefrontStyle[]): Record<string, unknown> | undefined {
    return dictOf(getActivePalette(styles ?? getActiveStorefrontStyles()), 'colors');
}

/** The active font pack's slot -> font-family map, or undefined when none is active. */
export function activeFontPackFonts(styles?: IStorefrontStyle[]): Record<string, unknown> | undefined {
    return dictOf(getActiveFontPack(styles ?? getActiveStorefrontStyles()), 'fonts');
}

/**
 * Cheap pre-check so the overwhelmingly common token-free value costs one typeof and one charCode.
 * Exported so the pickers can tell "this value is a token" without re-implementing the prefixes.
 */
export function isStyleToken(value: unknown): value is string {
    return typeof value === 'string'
        && value.charCodeAt(0) === 36 /* $ */
        && (value.indexOf(PALETTE_TOKEN_PREFIX) === 0 || value.indexOf(FONT_TOKEN_PREFIX) === 0);
}

/** The palette slot a `$palette:` token names (ignoring any `@alpha`), or undefined. */
export function paletteSlotOfToken(value: unknown): PaletteSlot | undefined {
    if (typeof value !== 'string' || value.indexOf(PALETTE_TOKEN_PREFIX) !== 0) return undefined;
    const body = value.slice(PALETTE_TOKEN_PREFIX.length);
    const at = body.indexOf('@');
    const slot = at >= 0 ? body.slice(0, at) : body;
    return (PALETTE_SLOTS as readonly string[]).indexOf(slot) >= 0 ? (slot as PaletteSlot) : undefined;
}

/**
 * Resolve a single `$palette:` / `$font:` token to its CSS value, or undefined when it cannot be
 * resolved (which means the property carrying it gets dropped).
 */
export function resolveStyleToken(
    value: string,
    styles: IStorefrontStyle[] | undefined = getActiveStorefrontStyles(),
): string | undefined {
    if (value.indexOf(FONT_TOKEN_PREFIX) === 0) {
        const slot = value.slice(FONT_TOKEN_PREFIX.length);
        if ((FONT_SLOTS as readonly string[]).indexOf(slot) < 0) return undefined;
        const font = activeFontPackFonts(styles)?.[slot];
        return typeof font === 'string' && font ? font : undefined;
    }
    if (value.indexOf(PALETTE_TOKEN_PREFIX) !== 0) return undefined;
    const body = value.slice(PALETTE_TOKEN_PREFIX.length);
    const at = body.indexOf('@');
    const slot = at >= 0 ? body.slice(0, at) : body;
    if ((PALETTE_SLOTS as readonly string[]).indexOf(slot) < 0) return undefined;
    const color = activePaletteColors(styles)?.[slot];
    if (typeof color !== 'string' || !color) return undefined;
    if (at < 0) return color;
    const rawAlpha = body.slice(at + 1);
    if (!ALPHA_PATTERN.test(rawAlpha)) return undefined;
    const alpha = Number(rawAlpha);
    if (!isFinite(alpha) || alpha < 0 || alpha > 1) return undefined;
    const rgb = hexRgb(color);
    if (!rgb) return undefined;
    return `rgba(${rgb.r},${rgb.g},${rgb.b},${alpha})`;
}

/**
 * Resolve every `$palette:` / `$font:` token among a style dictionary's own string values.
 *
 *  - No token anywhere  → the SAME object, by identity (a storefront with no palette allocates
 *                         nothing and every existing memo identity stays exactly as it was).
 *  - Resolvable token   → replaced with the concrete colour / font family.
 *  - Unresolvable token → the property is DELETED, so the element falls back to its default.
 *
 * Only the dictionary's own top-level values are considered; style dicts are flat camelCase CSS.
 * This runs as the LAST step of every resolver in this file, so a token is honored wherever it was
 * authored: in the named style's part, in the element's own dict, or in both.
 */
export function resolvePaletteTokens<T>(
    dict: T | undefined,
    styles: IStorefrontStyle[] | undefined = getActiveStorefrontStyles(),
): T | undefined {
    if (!dict || typeof dict !== 'object') return dict;
    const source = dict as unknown as Record<string, unknown>;
    let out: Record<string, unknown> | undefined;
    for (const key of Object.keys(source)) {
        const value = source[key];
        if (!isStyleToken(value)) continue;
        if (!out) out = { ...source };
        const resolved = resolveStyleToken(value, styles);
        if (resolved === undefined) delete out[key];
        else out[key] = resolved;
    }
    return (out ?? dict) as unknown as T;
}

/**
 * The storefront settings fields a token may be authored into. Deliberately an explicit list:
 * these are the colour/font-bearing fields, and nothing else on the settings object should ever be
 * rewritten by token resolution. Mirrors the renderer's TOKENIZED_SETTINGS_FIELDS exactly.
 */
export const TOKENIZED_SETTINGS_FIELDS = [
    'menuBarColor',
    'menuBarTextColor',
    'backgroundColor',
    'textColor',
    'sectionBackgroundColor',
    'sectionHeaderBackgroundColor',
    'sectionFooterBackgroundColor',
    'headerFont',
    'defaultFont',
] as const;

/**
 * Resolve tokens in the storefront settings' typed style fields. An unresolvable token leaves the
 * field UNSET, exactly as if the merchant had never filled it in. Settings with no tokens come back
 * by identity.
 *
 * NOTE for the admin: the pages editor reads these fields styleConfig-FIRST (styleConfig.ts's
 * `styleValue`), so the editor's actual choke point is `resolvedStyleValue` there, which resolves
 * one value through `resolveStyleToken`. This function is the object-level equivalent, kept for
 * parity with the renderer and for callers holding a plain settings object.
 */
export function resolveSettingsTokens<T>(
    settings: T,
    styles: IStorefrontStyle[] | undefined = getActiveStorefrontStyles(),
): T {
    if (!settings || typeof settings !== 'object') return settings;
    const source = settings as unknown as Record<string, unknown>;
    let out: Record<string, unknown> | undefined;
    for (const field of TOKENIZED_SETTINGS_FIELDS) {
        const value = source[field];
        if (!isStyleToken(value)) continue;
        if (!out) out = { ...source };
        const resolved = resolveStyleToken(value, styles);
        if (resolved === undefined) delete out[field];
        else out[field] = resolved;
    }
    return (out ?? settings) as unknown as T;
}

// camelCase slot -> kebab-case CSS custom property suffix (accentText -> accent-text).
const kebab = (name: string) => name.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);

// A value is only emitted into a <style> block when it can't terminate the declaration or rule it
// sits in. Merchant-authored data goes straight into a stylesheet here, so this stays strict.
const isSafeCssValue = (value: unknown): value is string =>
    typeof value === 'string' && !!value.trim() && !/[;{}<>]/.test(value);

/**
 * The `:root` custom properties for the active palette + font pack (`--sf-<slot>`, `--sf-<slot>-rgb`,
 * `--sf-font-heading` / `--sf-font-body`), ready for a <style> tag. Admin mirror of the renderer's
 * paletteCssVars: the pages editor injects this so preview surfaces authored against the vars (the
 * page-background wash, customCSS) repaint when the active palette changes. Undefined when neither
 * a palette nor a font pack is active.
 */
export function paletteCssVars(styles: IStorefrontStyle[] | undefined = getActiveStorefrontStyles()): string | undefined {
    const colors = activePaletteColors(styles);
    const fonts = activeFontPackFonts(styles);
    if (!colors && !fonts) return undefined;
    const declarations: string[] = [];
    if (colors) {
        for (const slot of PALETTE_SLOTS) {
            const value = colors[slot];
            if (!isSafeCssValue(value)) continue;
            const name = kebab(slot);
            declarations.push(`--sf-${name}: ${value.trim()};`);
            const rgb = hexRgb(value);
            if (rgb) declarations.push(`--sf-${name}-rgb: ${rgb.r},${rgb.g},${rgb.b};`);
        }
    }
    if (fonts) {
        for (const slot of FONT_SLOTS) {
            const value = fonts[slot];
            if (!isSafeCssValue(value)) continue;
            declarations.push(`--sf-font-${slot}: ${value.trim()};`);
        }
    }
    if (declarations.length === 0) return undefined;
    return `:root { ${declarations.join(' ')} }`;
}

/**
 * THE resolver. `local` is the element's own style dict (possibly carrying `$styleRef`), `part`
 * is the part implied by the key it lives on (undefined = use the referenced style's default
 * part), `styles` is the available style list.
 *
 *   no ref            -> local, unchanged
 *   ref, style found  -> { ...style.parts[part], ...local-without-$styleRef }   (local wins)
 *   ref, dangling     -> local-without-$styleRef
 *
 * The `$styleRef` key is stripped in every branch, and `$palette:`/`$font:` tokens are resolved
 * LAST - after the preset-under-local merge - so a token authored in either wins where it sits.
 */
export function resolveElementStyle<T>(
    local: T | undefined,
    part: string | undefined,
    styles: IStorefrontStyle[] | undefined,
): T | undefined {
    const ref = getStyleRef(local);
    if (!ref) return resolvePaletteTokens(stripStyleRef(local), styles);
    const stripped = stripStyleRef(local) as unknown as Record<string, unknown> | undefined;
    const style = findStyle(styles, ref);
    if (!style) return resolvePaletteTokens(stripped as unknown as T | undefined, styles);
    const resolvedPart = part ?? DEFAULT_STYLE_PART[style.elementType];
    const preset = (resolvedPart && style.parts) ? style.parts[resolvedPart] : undefined;
    if (!preset) return resolvePaletteTokens(stripped as unknown as T | undefined, styles);
    return resolvePaletteTokens({ ...preset, ...(stripped ?? {}) } as unknown as T, styles);
}

/**
 * Resolve the `hover` (or other state) part for the hover pipelines, which emit a scoped `:hover`
 * rule rather than an inline style. Admin mirror of the shopper renderer's `resolveHoverPart`
 * (ac-storefront-web stylesConfig.ts) — keep the semantics byte-for-byte equivalent.
 *
 * The ref may live on the hover dict itself, or — the common case — on the RESTING dict the hover
 * belongs to (`card` carries `$styleRef`, `cardHover` is the merchant's extra local hover tweak).
 * `refSource` supplies that second lookup; the hover dict's own ref wins. The preset's hover part
 * goes UNDER the local hover dict.
 *
 * Returns undefined when there is neither a local hover dict nor a preset hover part, so the
 * caller emits no rule at all.
 */
export function resolveHoverPart<T>(
    localHover: T | undefined,
    refSource?: unknown,
    styles?: IStorefrontStyle[],
    part = 'hover',
): T | undefined {
    const effective = styles ?? getActiveStorefrontStyles();
    const ref = getStyleRef(localHover) ?? getStyleRef(refSource);
    const local = stripStyleRef(localHover);
    if (!ref) return resolvePaletteTokens(local, effective);
    const preset = findStyle(effective, ref)?.parts?.[part];
    if (!preset) return resolvePaletteTokens(local, effective);
    return resolvePaletteTokens({ ...preset, ...((local ?? {}) as Record<string, unknown>) } as unknown as T, effective);
}

/**
 * A product card is drawn from several sibling elements (the tile, its name, its price, its image),
 * but a section only carries ONE dict for them — `productStyleProps`. So when that dict references
 * a card-type style, the dict itself overrides the style's `card` part, and the remaining parts are
 * taken straight from the style. No ref (or a dangling one) ⇒ only `card` is populated, from the
 * local dict, exactly as before.
 */
export interface IResolvedCardStyleGroup<T = Record<string, any>> {
    card?: T;
    title?: T;
    price?: T;
    image?: T;
    hover?: T;
}

export function resolveCardStyleGroup<T>(
    local: T | undefined,
    styles: IStorefrontStyle[] | undefined,
): IResolvedCardStyleGroup<T> {
    const style = findStyle(styles, getStyleRef(local));
    const parts = (style?.parts ?? {}) as Record<string, T | undefined>;
    // Every branch ends in token resolution - the sibling parts come straight off the named style,
    // so they need the same last step resolveElementStyle applies to the card part.
    return {
        card: resolveElementStyle(local, 'card', styles),
        title: resolvePaletteTokens(parts.title, styles),
        price: resolvePaletteTokens(parts.price, styles),
        image: resolvePaletteTokens(parts.image, styles),
        hover: resolvePaletteTokens(parts.hover, styles),
    };
}

/**
 * One page-level styling layer, as a specific SECTION sees it: the page's own value with that section's
 * override merged over it, then tokens resolved (AC-1165).
 *
 * `{ ...page, ...override }`, so an override states only what it changes and a section with none resolves
 * to exactly the page value it always did — which is the whole safety property of the feature and what
 * keeps every existing storefront rendering identically.
 *
 * An override may itself carry a `$styleRef`, so the merge runs through the normal element resolution
 * rather than being a plain spread: the named style's part goes under the override's own properties, and
 * the page value goes under both.
 */
export function resolveSectionOverride<T>(
    pageValue: T | undefined,
    overrides: { [configKey: string]: unknown } | undefined,
    configKey: string,
    styles: IStorefrontStyle[] | undefined,
): T | undefined {
    const override = overrides?.[configKey];
    if (override === undefined || override === null) return resolvePaletteTokens(pageValue, styles);
    const part = SECTION_OVERRIDE_PARTS[configKey];
    // Resolves the override's own $styleRef (if any) against the matching part, leaving plain CSS.
    const resolvedOverride = resolveElementStyle(override as T, part, styles);
    const page = (pageValue ?? {}) as Record<string, unknown>;
    return resolvePaletteTokens(
        { ...page, ...((resolvedOverride ?? {}) as Record<string, unknown>) } as unknown as T,
        styles);
}

/** Convenience wrapper for the section StyleProps dictionaries (identical semantics). */
export function resolveStyleProps<T>(
    local: T | undefined,
    key: string,
    styles: IStorefrontStyle[] | undefined,
): T | undefined {
    return resolveElementStyle(local, stylePartForKey(key), styles);
}

/**
 * How many `$styleRef`s point at `id` across the given roots (settings object with its JSON config
 * blobs, the working sections list, …). Style ids are GUIDs, so a substring count over the
 * serialized roots is both safe and cheap — the same trick the template remap uses server-side.
 */
export function countStyleRefs(id: string, roots: unknown[]): number {
    if (!id) return 0;
    let count = 0;
    for (const root of roots) {
        if (root === undefined || root === null) continue;
        let text: string;
        try {
            text = typeof root === 'string' ? root : JSON.stringify(root);
        } catch {
            continue;
        }
        if (!text) continue;
        let i = text.indexOf(id);
        while (i !== -1) {
            count++;
            i = text.indexOf(id, i + id.length);
        }
    }
    return count;
}

// ── Active-styles registry ───────────────────────────────────────────────────────────────
// The preview style builders in styleControls.tsx (elementStyleFrom / cardStyleFrom / …) are
// plain functions called from ~60 places across seven page editors. Rather than thread a styles
// array through every one of them (and every editor's prop contract), the pages editor publishes
// the currently-effective list — server styles merged with the Styles panel's unsaved drafts —
// into this module registry via <StorefrontStylesProvider> (StorefrontStylesContext.tsx), which
// re-renders the editor tree on change so the builders re-run against the new list.
//
// Components that need the list for UI (the style pickers, the panel) use the React context
// instead; this registry exists purely for the non-hook builders.

let activeStyles: IStorefrontStyle[] = [];

export function setActiveStorefrontStyles(styles: IStorefrontStyle[] | undefined): void {
    activeStyles = styles ?? [];
}

export function getActiveStorefrontStyles(): IStorefrontStyle[] {
    return activeStyles;
}

/** resolveElementStyle against the registry — what the preview builders call. */
export function resolveActiveStyle<T>(local: T | undefined, part?: string): T | undefined {
    return resolveElementStyle(local, part, activeStyles);
}

// ── Starter content for new styles ───────────────────────────────────────────────────────

/**
 * A reasonable starting point for a brand-new style of each element type. The preset types start
 * mostly empty (an empty dict per part); the token docs start POPULATED - an all-blank palette
 * would resolve every token to nothing, so a new one would silently do worse than no palette at all.
 */
export function emptyPartsFor(elementType: StorefrontStyleElementType): IStorefrontStyleParts {
    if (elementType === 'palette') return { colors: { ...DEFAULT_PALETTE_COLORS } };
    if (elementType === 'fontPack') return { fonts: { ...DEFAULT_FONT_PACK_FONTS } };
    const parts: IStorefrontStyleParts = {};
    for (const part of STYLE_PARTS[elementType]) parts[part] = {};
    return parts;
}

/** Next free sort order within an element type's list. */
export function nextSortOrder(styles: IStorefrontStyle[], elementType: StorefrontStyleElementType): number {
    const orders = styles.filter((s) => s.elementType === elementType).map((s) => s.sortOrder ?? 0);
    return orders.length ? Math.max(...orders) + 1 : 0;
}

/** Panel ordering: sortOrder, then name (so freshly-seeded styles land in a stable order). */
export function sortedStyles(styles: IStorefrontStyle[]): IStorefrontStyle[] {
    return [...styles].sort((a, b) =>
        (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || (a.name ?? '').localeCompare(b.name ?? ''));
}

/** The styles a picker for `target` should offer (matching element type, panel order). */
export function stylesForTarget(styles: IStorefrontStyle[], target?: IStyleTarget): IStorefrontStyle[] {
    if (!target) return [];
    return sortedStyles(styles.filter((s) => s.elementType === target.elementType));
}
